import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch } from "@/utils/api";
import { useAuth } from "@/state/AuthContext";
import type { AuthUser, TenantInfo } from "@/utils/permissions";

export function SignupPage() {
  const { token, setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (token) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await apiFetch<{
        token: string;
        user: AuthUser;
        tenant: TenantInfo;
      }>("/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, displayName, orgName, password }),
        token: null,
      });
      setSession(data.token, data.user, data.tenant);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="login-bg min-h-svh flex items-center justify-center p-4">
      <div className="login-card w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold text-center text-[#1e3a4f] mb-2">Create Your Organization</h1>
        <p className="text-center text-sm text-muted-foreground mb-6">
          Start a 7-day Starter trial — full access for up to 5 users.
        </p>
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="orgName">Organization Name</Label>
            <Input id="orgName" value={orgName} onChange={(e) => setOrgName(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Your Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="displayName">Display Name</Label>
            <Input id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" className="flex-1" asChild>
              <Link to="/login">Sign In</Link>
            </Button>
            <Button type="submit" className="flex-1 bg-[#1e3a4f] hover:bg-[#163041]" disabled={submitting}>
              {submitting ? "Creating…" : "Start Trial"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
